import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Card, Button, Form, Spinner, Alert, ProgressBar, Badge, InputGroup,
} from 'react-bootstrap';
import {
  FaArrowLeft, FaArrowRight, FaCheck, FaSearch, FaSyncAlt, FaUsers,
} from 'react-icons/fa';
import {
  startWizard, getWizard, advanceStep, provisionWorkspace, getTeamContext, searchSecurityGroups,
} from '@/api/wizardApi';
import type { WizardTeamContext, SgSearchResult, ProvisioningLog } from '@/types/workspace';

// ===== Step definitions =====
const STEPS = [
  { key: 'welcome',            label: 'Welcome' },
  { key: 'team',               label: 'Your Team' },
  { key: 'sg-selection',       label: 'Security Group' },
  { key: 'm365-group',         label: 'M365 Group' },
  { key: 'workspace-setup',    label: 'Workspace Setup' },
  { key: 'project-categories', label: 'Categories' },
  { key: 'kpi-mapping',        label: 'KPI Mapping' },
  { key: 'starter-projects',   label: 'Starter Projects' },
  { key: 'review',             label: 'Review & Provision' },
];

const TOTAL_STEPS = STEPS.length;

type StepData = Record<string, unknown>;

const statusColour = (s: string) => {
  if (s === 'Success') return 'success';
  if (s === 'Failed')  return 'danger';
  if (s === 'Pending') return 'warning';
  return 'secondary';
};

// ===== Main component =====
const LeaderWizard: React.FC = () => {
  const navigate = useNavigate();
  const { instanceId: routeInstanceId } = useParams<{ instanceId?: string }>();

  const [instanceId, setInstanceId]     = useState<string | null>(routeInstanceId ?? null);
  const [currentStep, setCurrentStep]   = useState(0);
  const [stepData, setStepData]         = useState<Record<string, StepData>>({});
  const [loading, setLoading]           = useState(true);
  const [submitting, setSubmitting]     = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionLogs, setProvisionLogs] = useState<ProvisioningLog[]>([]);
  const [provisionDone, setProvisionDone] = useState(false);
  const [workspaceId, setWorkspaceId]   = useState<string | null>(null);
  const [error, setError]               = useState<string | null>(null);

  // Step-specific state
  const [teamContext, setTeamContext]   = useState<WizardTeamContext | null>(null);
  const [sgSearch, setSgSearch]         = useState('');
  const [sgResults, setSgResults]       = useState<SgSearchResult[]>([]);
  const [sgSearching, setSgSearching]   = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Init: start or resume wizard ──
  useEffect(() => {
    (async () => {
      try {
        if (routeInstanceId && routeInstanceId !== 'new') {
          // Resume existing instance
          const inst = await getWizard(routeInstanceId);
          setCurrentStep(inst.currentStep);
          if (inst.stepDataJson) {
            try { setStepData(JSON.parse(inst.stepDataJson)); } catch { /* ignore */ }
          }
          if (inst.status === 'Completed' && inst.workspaceId) {
            setProvisionDone(true);
            setWorkspaceId(inst.workspaceId);
          }
        } else {
          // New instance
          const { instanceId: newId } = await startWizard('Leader');
          setInstanceId(newId);
          navigate(`/workspaces/wizard/leader/${newId}`, { replace: true });
        }
      } catch {
        setError('Failed to start wizard. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ── Load team context on step 1 ──
  useEffect(() => {
    if (currentStep === 1 && !teamContext) {
      getTeamContext().then(setTeamContext).catch(() => null);
    }
  }, [currentStep]);

  // ── SG search debounce ──
  useEffect(() => {
    if (sgSearch.length < 2) { setSgResults([]); return; }
    const timer = setTimeout(async () => {
      setSgSearching(true);
      try { setSgResults(await searchSecurityGroups(sgSearch)); }
      catch { setSgResults([]); }
      finally { setSgSearching(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [sgSearch]);

  // ── Step field helpers ──
  const getField = (stepKey: string, field: string): unknown =>
    (stepData[stepKey] as Record<string, unknown>)?.[field];

  const setField = (stepKey: string, field: string, value: unknown) =>
    setStepData(prev => ({
      ...prev,
      [stepKey]: { ...(prev[stepKey] ?? {}), [field]: value },
    }));

  // ── Advance to next step ──
  const handleNext = useCallback(async () => {
    if (!instanceId) return;
    const step = STEPS[currentStep];
    setSubmitting(true);
    setError(null);
    try {
      const data = (stepData[step.key] ?? {}) as Record<string, unknown>;
      await advanceStep(instanceId, step.key, data);
      setCurrentStep(s => s + 1);
    } catch {
      setError('Failed to save step. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [instanceId, currentStep, stepData]);

  // ── Trigger provisioning ──
  const handleProvision = useCallback(async () => {
    if (!instanceId) return;
    setProvisioning(true);
    setError(null);

    // Poll every 3s for log updates
    pollRef.current = setInterval(async () => {
      try {
        const inst = await getWizard(instanceId);
        setProvisionLogs(inst.provisioningLogs);
        if (inst.status === 'Completed' && inst.workspaceId) {
          clearInterval(pollRef.current!);
          setProvisionDone(true);
          setWorkspaceId(inst.workspaceId);
          setProvisioning(false);
        }
      } catch { /* keep polling */ }
    }, 3000);

    try {
      const { workspaceId: wid } = await provisionWorkspace(instanceId);
      clearInterval(pollRef.current!);
      setWorkspaceId(wid);
      setProvisionDone(true);
      setProvisioning(false);
      // Final log fetch
      const inst = await getWizard(instanceId);
      setProvisionLogs(inst.provisioningLogs);
    } catch (ex: unknown) {
      clearInterval(pollRef.current!);
      setProvisioning(false);
      setError('Provisioning encountered an error. Check the steps below.');
      // Still try to fetch logs
      try {
        const inst = await getWizard(instanceId);
        setProvisionLogs(inst.provisioningLogs);
      } catch { /* ignore */ }
    }
  }, [instanceId]);

  if (loading) {
    return <Container className="py-5 text-center"><Spinner animation="border" /></Container>;
  }

  // ── Completed ──
  if (provisionDone && workspaceId) {
    return (
      <Container className="py-5" style={{ maxWidth: 700 }}>
        <Card className="border-0 shadow-sm text-center p-5">
          <div className="text-success mb-3" style={{ fontSize: 64 }}><FaCheck /></div>
          <h3 className="fw-bold mb-2">Workspace ready!</h3>
          <p className="text-muted mb-4">
            Your Security Group, M365 Group, SharePoint lists, and Planner board are all set up.
          </p>
          <Button variant="primary" onClick={() => navigate(`/workspaces/${workspaceId}`)}>
            Open your workspace <FaArrowRight className="ms-2" />
          </Button>
        </Card>
      </Container>
    );
  }

  // ── Provisioning in progress ──
  if (provisioning || (currentStep >= TOTAL_STEPS && !provisionDone)) {
    return (
      <Container className="py-4" style={{ maxWidth: 700 }}>
        <h4 className="fw-bold mb-4">Setting up your workspace…</h4>
        {provisionLogs.map(log => (
          <div key={log.logId} className="d-flex align-items-center gap-3 mb-2">
            <Badge bg={statusColour(log.status)} style={{ minWidth: 70 }}>{log.status}</Badge>
            <span>{log.action}</span>
            {log.errorMessage && <small className="text-danger">{log.errorMessage}</small>}
          </div>
        ))}
        {provisioning && (
          <div className="d-flex align-items-center gap-2 mt-3 text-muted">
            <Spinner animation="border" size="sm" />
            <span>Working…</span>
          </div>
        )}
        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      </Container>
    );
  }

  const stepKey = STEPS[currentStep]?.key;
  const progress = Math.round(((currentStep) / TOTAL_STEPS) * 100);

  return (
    <Container className="py-4" style={{ maxWidth: 800 }}>
      {/* Progress */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <small className="text-muted">Step {currentStep + 1} of {TOTAL_STEPS}: <strong>{STEPS[currentStep]?.label}</strong></small>
          <small className="text-muted">{progress}%</small>
        </div>
        <ProgressBar now={progress} variant="primary" style={{ height: 6 }} />
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-4">
          {/* ── STEP 0: Welcome ── */}
          {stepKey === 'welcome' && (
            <div>
              <h4 className="fw-bold mb-3">Welcome to WorkOS</h4>
              <p>This wizard takes about 5 minutes and sets up a fully connected team workspace.</p>
              <p>You'll end up with:</p>
              <ul>
                <li>A <strong>Security Group</strong> — share once, your whole team gets access</li>
                <li>An <strong>M365 Group</strong> with a connected SharePoint site and Teams space</li>
                <li><strong>10 structured lists</strong> — Projects, Tasks, KPIs, Goals, Risks, Decisions, and more</li>
                <li>A <strong>Planner board</strong> linked to your workspace</li>
              </ul>
              <p className="text-muted small">
                Each step saves automatically. You can close and resume at any time.
              </p>
            </div>
          )}

          {/* ── STEP 1: Team ── */}
          {stepKey === 'team' && (
            <div>
              <h5 className="fw-bold mb-3"><FaUsers className="me-2" />Your Team</h5>
              {!teamContext ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /> Loading team info…</div>
              ) : (
                <>
                  {teamContext.manager && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Your Manager</label>
                      <div className="p-2 rounded bg-light small">{teamContext.manager.displayName} ({teamContext.manager.mail})</div>
                    </div>
                  )}
                  <div>
                    <label className="form-label fw-semibold">Direct Reports ({teamContext.directReports.length})</label>
                    {teamContext.directReports.length === 0 ? (
                      <p className="text-muted small">No direct reports found in your org chart. You can still continue.</p>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {teamContext.directReports.map(u => (
                          <Badge key={u.id} bg="secondary" className="fw-normal">{u.displayName}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── STEP 2: Security Group ── */}
          {stepKey === 'sg-selection' && (
            <div>
              <h5 className="fw-bold mb-1">Security Group</h5>
              <p className="text-muted small mb-3">
                A team Security Group means you share once — everyone on your team gets access automatically.
                Use it in your email signature, on projects, anywhere you'd normally add individuals.
              </p>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Search for an existing SG</Form.Label>
                <InputGroup>
                  <Form.Control
                    placeholder="Type SG name…"
                    value={sgSearch}
                    onChange={e => setSgSearch(e.target.value)}
                  />
                  <InputGroup.Text>{sgSearching ? <Spinner animation="border" size="sm" /> : <FaSearch />}</InputGroup.Text>
                </InputGroup>
                {sgSearch.length >= 2 && !sgSearching && sgResults.length === 0 && (
                  <div className="text-muted small mt-1 px-1">No security groups found matching "{sgSearch}"</div>
                )}
                {sgResults.length > 0 && (
                  <div className="border rounded mt-1" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {sgResults.map(sg => (
                      <div
                        key={sg.id}
                        className={`px-3 py-2 small cursor-pointer ${getField('sg-selection', 'existingSecurityGroupId') === sg.id ? 'bg-primary text-white' : 'hover-bg-light'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setField('sg-selection', 'existingSecurityGroupId', sg.id);
                          setField('sg-selection', 'existingSecurityGroupName', sg.displayName);
                          setField('sg-selection', 'newSecurityGroupName', '');
                        }}
                      >
                        {sg.displayName}
                      </div>
                    ))}
                  </div>
                )}
              </Form.Group>
              <div className="text-muted small my-2 text-center">— or create a new one —</div>
              <Form.Group>
                <Form.Label className="fw-semibold">New Security Group name</Form.Label>
                <Form.Control
                  placeholder="e.g. Engineering Team"
                  value={(getField('sg-selection', 'newSecurityGroupName') as string) ?? ''}
                  onChange={e => {
                    setField('sg-selection', 'newSecurityGroupName', e.target.value);
                    setField('sg-selection', 'existingSecurityGroupId', '');
                  }}
                />
              </Form.Group>
            </div>
          )}

          {/* ── STEP 3: M365 Group ── */}
          {stepKey === 'm365-group' && (
            <div>
              <h5 className="fw-bold mb-1">M365 Group</h5>
              <p className="text-muted small mb-3">
                Your workspace will be linked to an M365 Group (gives you SharePoint, Teams, and Planner in one place).
                Enter an existing group name to link to it, or leave blank to create a new one.
              </p>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Existing M365 Group ID (optional)</Form.Label>
                <Form.Control
                  placeholder="Paste group object ID to link existing…"
                  value={(getField('m365-group', 'existingM365GroupId') as string) ?? ''}
                  onChange={e => setField('m365-group', 'existingM365GroupId', e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="fw-semibold">New group name</Form.Label>
                <Form.Control
                  placeholder="e.g. Engineering"
                  value={(getField('m365-group', 'newGroupName') as string) ?? ''}
                  onChange={e => setField('m365-group', 'newGroupName', e.target.value)}
                />
                <Form.Text className="text-muted">
                  Leave blank to use the workspace name automatically.
                </Form.Text>
              </Form.Group>
            </div>
          )}

          {/* ── STEP 4: Workspace Setup ── */}
          {stepKey === 'workspace-setup' && (
            <div>
              <h5 className="fw-bold mb-3">Workspace Setup</h5>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Workspace Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  placeholder="e.g. Engineering Team"
                  value={(getField('workspace-setup', 'displayName') as string) ?? ''}
                  onChange={e => setField('workspace-setup', 'displayName', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="What does this team do?"
                  value={(getField('workspace-setup', 'description') as string) ?? ''}
                  onChange={e => setField('workspace-setup', 'description', e.target.value)}
                />
              </Form.Group>
              <div className="row g-3">
                <div className="col-md-6">
                  <Form.Label className="fw-semibold">Business Unit</Form.Label>
                  <Form.Control
                    placeholder="e.g. Operations"
                    value={(getField('workspace-setup', 'businessUnit') as string) ?? ''}
                    onChange={e => setField('workspace-setup', 'businessUnit', e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label className="fw-semibold">Cost Center</Form.Label>
                  <Form.Control
                    placeholder="e.g. 4200"
                    value={(getField('workspace-setup', 'costCenter') as string) ?? ''}
                    onChange={e => setField('workspace-setup', 'costCenter', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 5: Project Categories ── */}
          {stepKey === 'project-categories' && (
            <div>
              <h5 className="fw-bold mb-2">Project Categories</h5>
              <p className="text-muted small mb-3">
                These become the category options in your Projects list. You can update them later.
              </p>
              {['Operations', 'Support', 'Maintenance', 'Strategic'].map(cat => {
                const selected = ((getField('project-categories', 'categories') as string[]) ?? ['Operations', 'Support', 'Maintenance', 'Strategic']).includes(cat);
                return (
                  <Form.Check
                    key={cat}
                    type="checkbox"
                    label={cat}
                    checked={selected}
                    onChange={e => {
                      const current = ((getField('project-categories', 'categories') as string[]) ?? ['Operations', 'Support', 'Maintenance', 'Strategic']);
                      setField('project-categories', 'categories',
                        e.target.checked ? [...current, cat] : current.filter(c => c !== cat)
                      );
                    }}
                  />
                );
              })}
            </div>
          )}

          {/* ── STEP 6: KPI Mapping ── */}
          {stepKey === 'kpi-mapping' && (
            <div>
              <h5 className="fw-bold mb-2">Organizational KPI Mapping</h5>
              <p className="text-muted small mb-3">
                Which organizational goals does your team support? You'll map your team's KPIs to these after setup.
              </p>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Paste or type organizational goal names, one per line…"
                value={(getField('kpi-mapping', 'orgGoals') as string) ?? ''}
                onChange={e => setField('kpi-mapping', 'orgGoals', e.target.value)}
              />
              <Form.Text className="text-muted">Optional — you can add these later.</Form.Text>
            </div>
          )}

          {/* ── STEP 7: Starter Projects ── */}
          {stepKey === 'starter-projects' && (
            <div>
              <h5 className="fw-bold mb-2">Starter Projects</h5>
              <p className="text-muted small mb-3">
                Add 1–3 initial projects to your workspace. You can skip this and add them after setup.
              </p>
              {[0, 1, 2].map(i => (
                <Form.Group key={i} className="mb-2">
                  <Form.Control
                    placeholder={`Project ${i + 1} name…`}
                    value={(getField('starter-projects', `project${i}`) as string) ?? ''}
                    onChange={e => setField('starter-projects', `project${i}`, e.target.value)}
                  />
                </Form.Group>
              ))}
            </div>
          )}

          {/* ── STEP 8: Review & Provision ── */}
          {stepKey === 'review' && (
            <div>
              <h5 className="fw-bold mb-3">Review & Provision</h5>
              <p className="text-muted small mb-3">
                Everything looks good? Hit <strong>Create Workspace</strong> to provision all the pieces.
                This takes about 30–60 seconds.
              </p>
              <table className="table table-sm table-bordered small mb-4">
                <tbody>
                  <tr><td className="fw-semibold" style={{ width: '40%' }}>Workspace name</td><td>{(getField('workspace-setup', 'displayName') as string) || '(not set)'}</td></tr>
                  <tr><td className="fw-semibold">Business Unit</td><td>{(getField('workspace-setup', 'businessUnit') as string) || '—'}</td></tr>
                  <tr><td className="fw-semibold">Cost Center</td><td>{(getField('workspace-setup', 'costCenter') as string) || '—'}</td></tr>
                  <tr><td className="fw-semibold">Security Group</td><td>{(getField('sg-selection', 'existingSecurityGroupName') as string) || (getField('sg-selection', 'newSecurityGroupName') as string) || '(auto-generated)'}</td></tr>
                  <tr><td className="fw-semibold">M365 Group</td><td>{(getField('m365-group', 'newGroupName') as string) || (getField('workspace-setup', 'displayName') as string) || '(auto-generated)'}</td></tr>
                  <tr><td className="fw-semibold">Lists to create</td><td>Projects, Tasks, KPIs, Goals, Risks, Issues, Decisions, Meetings, MeetingActions, WorkspaceSettings</td></tr>
                  <tr><td className="fw-semibold">Planner buckets</td><td>To Do, In Progress, Done, Blocked</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Navigation */}
      <div className="d-flex justify-content-between mt-4">
        <Button
          variant="outline-secondary"
          disabled={currentStep === 0 || submitting}
          onClick={() => setCurrentStep(s => s - 1)}
        >
          <FaArrowLeft className="me-2" />Back
        </Button>

        {stepKey !== 'review' ? (
          <Button variant="primary" onClick={handleNext} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Next <FaArrowRight className="ms-2" />
          </Button>
        ) : (
          <Button variant="success" onClick={handleProvision} disabled={provisioning}>
            {provisioning ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSyncAlt className="me-2" />}
            Create Workspace
          </Button>
        )}
      </div>
    </Container>
  );
};

export default LeaderWizard;
